import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { normalizeString } from 'utils/common';

export const useAdminLocation = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();

  const params = useParams();

  const showName = !!params?.shownName;

  const paramsValues = useMemo(
    () =>
      Object.values(params)
        .filter((item) => item !== undefined)
        .map(normalizeString),
    [params]
  );

  const toLowerCamelCase = (segment: string) =>
    segment.includes('-')
      ? segment
          .split('-')
          .map((part, index) => (index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
          .join('')
      : segment;

  const filteredPathnameArr = useMemo(() => {
    const res = pathname
      .split('/')
      .filter(Boolean)
      .map(decodeURIComponent)
      .map(normalizeString)
      .filter((item) => !['edit', 'new', 'delete', 'create', 'condition'].includes(item) && !paramsValues.includes(item));

    const withoutTrailingNumeric = res.length < 2 || /^\d+$/.test(res[res.length - 1]) ? res.slice(0, -1) : res;

    return withoutTrailingNumeric.filter((item) => !(item === 'templates' && withoutTrailingNumeric.includes('rest-api-connections')));
  }, [paramsValues, pathname]);

  const pageNames = useMemo(() => {
    if (!filteredPathnameArr) return [];

    const translationKeys = filteredPathnameArr.map(toLowerCamelCase).map((_, index, array) => {
      if (index === 0) return 'Admin';

      const pathSegments = array.slice(0, index + 1).join('.');
      const baseKey = `sidebar.${pathSegments}`;
      return i18n.exists(`${baseKey}._`) ? `${baseKey}._` : baseKey;
    });

    return translationKeys.map((key) => (i18n.exists(key) ? t(key) : key));
  }, [filteredPathnameArr, i18n, t]);

  return { pageNames, title: `${pageNames.at(-1)} ${showName ? `- ${params?.shownName}` : ''}`, filteredPathnameArr };
};
